import React, { useMemo, useState, useEffect } from 'react';
import { Table, Row, Col, Tooltip, User, Text, Input, Dropdown, Container, Spacer, styled, Loading } from "@nextui-org/react";
import { FaEdit, FaBan, FaCheck, FaUserShield, FaSearch } from 'react-icons/fa';
import { IconButton } from './IconButton';
import { getUsers } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

// 1. Define Types
interface UserData {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Regular';
  status: 'Active' | 'Suspended' | 'Pending';
  avatar: string;
}

// Custom Badge Component
const StyledBadge = styled('span', {
  display: 'inline-block',
  textTransform: 'uppercase',
  padding: '5px 8px',
  margin: '0 2px',
  fontSize: '10px',
  fontWeight: '800',
  borderRadius: '14px',
  letterSpacing: '0.6px',
  lineHeight: 1,
  boxShadow: '1px 2px 5px 0px rgb(0 0 0 / 5%)',
  alignItems: 'center',
  alignSelf: 'center',
  variants: {
    type: {
      active: {
        bg: '$successLight',
        color: '$success'
      },
      suspended: {
        bg: '$errorLight',
        color: '$error'
      },
      pending: {
        bg: '$warningLight',
        color: '$warning'
      }
    }
  }
});

const UserAdminPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    const fetchUsers = async () => {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const token = await user.getIdToken();
        const response = await getUsers(token);

        const fetchedUsers: UserData[] = response.users.map((u: any) => ({
          id: u.uid,
          name: u.displayName || 'Unknown',
          email: u.email || 'No email',
          role: u.customClaims?.admin ? 'Admin' : 'Regular',
          status: u.disabled ? 'Suspended' : 'Active', // Assume active if not disabled
          avatar: u.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.displayName || u.email || 'Unknown')}&background=random`,
        }));

        setData(fetchedUsers);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch users.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [user]);

  // Filtering logic
  const filteredData = useMemo(() => {
    return data.filter((user) => {
      const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      const matchesStatus = statusFilter === "all" || user.status === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [data, searchQuery, roleFilter, statusFilter]);

  // Actions
  const handleEdit = (id: string) => {
    console.log("Edit user", id);
    // Placeholder for edit action
  };

  const handleToggleStatus = (id: string) => {
    setData(prev => prev.map(user => {
      if (user.id === id) {
        return { ...user, status: user.status === 'Active' ? 'Suspended' : 'Active' };
      }
      return user;
    }));
  };

  const handleToggleRole = (id: string) => {
    setData(prev => prev.map(user => {
      if (user.id === id) {
        return { ...user, role: user.role === 'Admin' ? 'Regular' : 'Admin' };
      }
      return user;
    }));
  };

  const renderCell = (user: UserData, columnKey: React.Key) => {
    const cellValue = user[columnKey as keyof UserData];
    switch (columnKey) {
      case "name":
        return (
          <User squared src={user.avatar} name={cellValue} css={{ p: 0 }}>
            {user.email}
          </User>
        );
      case "role":
        return (
          <Col>
            <Row>
              <Text b size={14} css={{ tt: "capitalize" }}>
                {cellValue}
              </Text>
            </Row>
            <Row>
              <Text b size={13} css={{ tt: "capitalize", color: "$accents7" }}>
                {user.role}
              </Text>
            </Row>
          </Col>
        );
      case "status":
        return (
          <StyledBadge type={user.status === 'Active' ? 'active' : user.status === 'Suspended' ? 'suspended' : 'pending'}>
            {user.status}
          </StyledBadge>
        );
      case "actions":
        return (
          <Row justify="center" align="center">
            <Col css={{ d: "flex" }}>
              <Tooltip content="Edit user">
                <IconButton onClick={() => handleEdit(user.id)}>
                  <FaEdit size={20} fill="#979797" />
                </IconButton>
              </Tooltip>
            </Col>
            <Col css={{ d: "flex" }}>
              <Tooltip content={user.status === 'Active' ? "Suspend user" : "Activate user"}>
                <IconButton onClick={() => handleToggleStatus(user.id)}>
                  {user.status === 'Active' ? (
                    <FaBan size={20} fill="#FF0080" />
                  ) : (
                    <FaCheck size={20} fill="#17C964" />
                  )}
                </IconButton>
              </Tooltip>
            </Col>
            <Col css={{ d: "flex" }}>
              <Tooltip content={user.role === 'Regular' ? "Promote to Admin" : "Demote to Regular"}>
                <IconButton onClick={() => handleToggleRole(user.id)}>
                  <FaUserShield size={20} fill={user.role === 'Admin' ? "#F5A623" : "#979797"} />
                </IconButton>
              </Tooltip>
            </Col>
          </Row>
        );
      default:
        return cellValue;
    }
  };

  return (
    <Container css={{ padding: "$10" }}>
      <Text h2>User Administration</Text>
      <Spacer y={1} />

      {loading && <Loading>Loading users...</Loading>}
      {error && <Text color="error">{error}</Text>}

      {/* Controls */}
      <Row justify="space-between" align="center" css={{ mb: "$8" }}>
        <Col css={{ width: "40%" }}>
          <Input
            clearable
            contentLeft={<FaSearch fill="var(--nextui-colors-accents6)" />}
            contentLeftStyling={false}
            css={{ w: "100%", "@xsMax": { mw: "300px" } }}
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Col>
        <Col css={{ width: "auto", display: "flex", gap: "$5" }}>
          <Dropdown>
            <Dropdown.Button flat>{roleFilter === "all" ? "Role: All" : `Role: ${roleFilter}`}</Dropdown.Button>
            <Dropdown.Menu
              aria-label="Role Filter"
              selectionMode="single"
              selectedKeys={[roleFilter]}
              onSelectionChange={(keys) => setRoleFilter(Array.from(keys)[0] as string)}
            >
              <Dropdown.Item key="all">All</Dropdown.Item>
              <Dropdown.Item key="Admin">Admin</Dropdown.Item>
              <Dropdown.Item key="Regular">Regular</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown>
            <Dropdown.Button flat>{statusFilter === "all" ? "Status: All" : `Status: ${statusFilter}`}</Dropdown.Button>
            <Dropdown.Menu
              aria-label="Status Filter"
              selectionMode="single"
              selectedKeys={[statusFilter]}
              onSelectionChange={(keys) => setStatusFilter(Array.from(keys)[0] as string)}
            >
              <Dropdown.Item key="all">All</Dropdown.Item>
              <Dropdown.Item key="Active">Active</Dropdown.Item>
              <Dropdown.Item key="Suspended">Suspended</Dropdown.Item>
              <Dropdown.Item key="Pending">Pending</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>

      {/* Table */}
      <Table
        aria-label="User administration table"
        css={{
          height: "auto",
          minWidth: "100%",
        }}
        selectionMode="none"
      >
        <Table.Header>
          <Table.Column key="name">NAME</Table.Column>
          <Table.Column key="role">ROLE</Table.Column>
          <Table.Column key="status">STATUS</Table.Column>
          <Table.Column key="actions" align="center">ACTIONS</Table.Column>
        </Table.Header>
        <Table.Body items={filteredData}>
          {(item: UserData) => (
            <Table.Row key={item.id}>
              {(columnKey: React.Key) => (
                <Table.Cell>
                  {renderCell(item, columnKey)}
                </Table.Cell>
              )}
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Container>
  );
};

export default UserAdminPage;
